import { Box, Button, Chip, CircularProgress, Divider } from "@mui/material";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useConstants } from "../hooks/UseConstants";
import Fetch from "../services/Fetch";
import { useWaits } from "../hooks/UseWait";
import { FormattedMessage } from "react-intl";
import {
    DetailInfoRow,
    DetailMediaPanel,
    DetailModalShell,
    DetailSection,
} from "../components/admin/AdminDetailParts";

function CategoryDetails({ onClickClose, onClickUpdate, category, setSnackBar, setCategories }) {
    const { language, host } = useConstants();
    const { sendWait, setSendWait } = useWaits();
    const isAr = language === "ar";

    const deleteCategory = async () => {
        setSendWait(true);
        const result = await Fetch(host + `/admin/categories/${category.id}/delete`, "DELETE", null);

        if (result.status === 200) {
            setCategories((list) => list.filter((c) => c.id !== category.id));
            setSnackBar("success", <FormattedMessage id="category_deleted_msg" />);
            onClickClose();
        } else {
            setSnackBar("error", result.data?.errors?.[0] || result.data?.message || "Error");
        }

        setSendWait(false);
    };

    if (!category) return null;

    const name = language === "en" ? category.name_en : category.name_ar;
    const descriptionAr = category.description_ar || (isAr ? "لا يوجد وصف بالعربية." : "No Arabic description.");
    const descriptionEn = category.description_en || (isAr ? "لا يوجد وصف بالإنجليزية." : "No English description.");

    return (
        <DetailModalShell
            isAr={isAr}
            icon={<CategoryOutlinedIcon sx={{ fontSize: 32 }} />}
            title={<FormattedMessage id="category_details" />}
            subtitle={isAr ? "مراجعة بيانات الفئة والتخصصات المرتبطة." : "Review category information and linked majors."}
            onClose={onClickClose}
        >
            <DetailMediaPanel
                image={category.image}
                fallbackIcon={<CategoryOutlinedIcon sx={{ fontSize: 56, color: "#94a3b8" }} />}
                title={name}
                description={language === "en" ? descriptionEn : descriptionAr}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mb: 3 }}>
                <DetailSection icon={<CategoryOutlinedIcon />} title={isAr ? "الوصف" : "Descriptions"}>
                    <DetailInfoRow label={isAr ? "الوصف بالعربية" : "Arabic description"} value={descriptionAr} />
                    <DetailInfoRow label={isAr ? "الوصف بالإنجليزية" : "English description"} value={descriptionEn} isLast />
                </DetailSection>

                <DetailSection icon={<SchoolOutlinedIcon />} title={<FormattedMessage id="linked_majors" />}>
                    {category.majors?.length ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {category.majors.map((m) => (
                                <Chip
                                    key={m.id}
                                    size="small"
                                    label={language === "en" ? m.name_en : m.name_ar}
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                />
                            ))}
                        </Box>
                    ) : (
                        <DetailInfoRow label={isAr ? "التخصصات" : "Majors"} value={isAr ? "لا توجد تخصصات مرتبطة" : "No linked majors"} isLast />
                    )}
                </DetailSection>
            </Box>

            <Divider sx={{ mb: 2.5 }} />

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "flex-end" }}>
                <Button
                    variant="outlined"
                    startIcon={<EditOutlinedIcon />}
                    sx={{ minWidth: 140 }}
                    onClick={() => {
                        onClickUpdate(category.id);
                        onClickClose();
                    }}
                >
                    <FormattedMessage id="update" />
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    startIcon={sendWait ? <CircularProgress size={18} color="inherit" /> : <DeleteOutlineIcon />}
                    sx={{ minWidth: 150 }}
                    onClick={deleteCategory}
                    disabled={sendWait}
                >
                    {sendWait ? (isAr ? "جاري الحذف..." : "Deleting...") : <FormattedMessage id="delete" />}
                </Button>
            </Box>
        </DetailModalShell>
    );
}

export default CategoryDetails;
